import { Box, Editor, TLShape } from "tldraw";
import { convertTldrawIdToSimpleId } from "../../shared/format/convertTldrawShapeToBlurryShape";
import { ordinalOf } from "./labels";
import { RenderBatch, rasterizeInkCrop } from "./rasterize";
import {
  StudentInkCluster,
  isStudentInk,
  strokeKey,
} from "./studentInkClusters";

/** Long side to render at. Real detail: it re-renders from the vector path. */
const TARGET_PX = 768;
/** Ceiling on the enlargement, so one digit does not fill a frame edge to edge. */
const MAX_SCALE = 4;
/**
 * Wall-clock the crop pass may spend before it stops starting new renders.
 *
 * Not measured — an estimate, and the `console.debug` below is what replaces it
 * with a real number. Reasoning: this sits on time-to-first-token for a voice
 * turn, the page screenshot has already spent its own renders, and ~150ms is
 * about where added latency stops being invisible. Truncation is cheap because
 * the cache picks the rest up next turn.
 */
const BUDGET_MS = 150;

/** Blank paper kept round the writing, so a glyph is not pressed against the edge. */
const PAD_PX = 12;

/** One cluster's handwriting, rendered on its own. */
export interface InkCrop {
  label: string;
  /** JPEG data URL: this cluster's strokes, black, on blank paper. */
  image: string;
  w: number;
  h: number;
  /** Touched since the model last looked. Not in the cache key — same pixels. */
  changed: boolean;
}

/** Keyed by CONTENT, so writing the student has not touched renders once. */
const caches = new WeakMap<object, Map<string, InkCrop>>();

/**
 * Crop keys that would not render. Same content key as the cache, so a cluster
 * the student edits is retried; without this a cluster that cannot rasterize is
 * re-attempted in full every turn, forever.
 */
const failures = new WeakMap<object, Set<string>>();

function cacheFor(owner: object): Map<string, InkCrop> {
  let cache = caches.get(owner);
  if (!cache) caches.set(owner, (cache = new Map()));
  return cache;
}

function failuresFor(owner: object): Set<string> {
  let failed = failures.get(owner);
  if (!failed) failures.set(owner, (failed = new Set()));
  return failed;
}

/** Label is in the key: a crop is only ever shown beside its id. */
function cropKey(
  cluster: StudentInkCluster,
  bounds: Box,
  scale: number,
): string {
  const box = [bounds.x, bounds.y, bounds.w, bounds.h].map(Math.round);
  return `${cluster.label}|${strokeKey(cluster)}|${box.join(",")}|${scale.toFixed(2)}`;
}

function cropBounds(cluster: StudentInkCluster): Box {
  const { x, y, w, h } = cluster.bounds;
  return new Box(x - PAD_PX, y - PAD_PX, w + 2 * PAD_PX, h + 2 * PAD_PX);
}

function cropScale(bounds: Box): number {
  const longest = Math.max(bounds.w, bounds.h);
  return longest > 0
    ? Math.min(MAX_SCALE, Math.max(1, TARGET_PX / longest))
    : 1;
}

/** Most recent first: ordinals are minted in order and never recycled. */
export function byRecency(clusters: StudentInkCluster[]): StudentInkCluster[] {
  return [...clusters].sort((a, b) => ordinalOf(b.label) - ordinalOf(a.label));
}

/**
 * Every cluster in view, one image each, most recent first. Uncapped: a crop
 * that was never rendered cannot be named in `verify`'s `ink`. The cost is
 * render time rather than bytes, which is what the cache is for.
 */
export async function computeInkCrops(
  editor: Editor,
  owner: object,
  clusters: StudentInkCluster[],
  opts: { renderBatch: RenderBatch; onCropBoundary?: () => Promise<void> },
): Promise<InkCrop[]> {
  const cache = cacheFor(owner);
  if (clusters.length === 0) {
    // Still an eviction — the writing is gone or out of view.
    cache.clear();
    return [];
  }

  // Clusters carry SIMPLE ids; rendering needs the shapes behind them.
  const strokesById = new Map<string, TLShape>();
  for (const shape of editor.getCurrentPageShapesSorted()) {
    if (isStudentInk(shape)) {
      strokesById.set(convertTldrawIdToSimpleId(shape.id), shape);
    }
  }

  // Changed first, then recency: a budget must never drop writing the student
  // just touched in favour of untouched newer work.
  const wanted = byRecency(clusters).sort(
    (a, b) => Number(b.changed) - Number(a.changed),
  );
  const failed = failuresFor(owner);
  const crops: InkCrop[] = [];
  const live = new Set<string>();
  const startedAt = performance.now();
  let rendered = 0;
  let overBudget = 0;
  let unrenderable = 0;

  for (const [index, cluster] of wanted.entries()) {
    const bounds = cropBounds(cluster);
    const scale = cropScale(bounds);
    const key = cropKey(cluster, bounds, scale);
    live.add(key);

    if (failed.has(key)) {
      unrenderable++;
      continue;
    }

    const cached = cache.get(key);
    if (cached) {
      // `changed` is a fact about the turn, so re-stamp rather than reuse.
      crops.push({ ...cached, changed: cluster.changed });
      continue;
    }

    const strokes = cluster.strokeIds
      .map((id) => strokesById.get(id))
      .filter((shape): shape is TLShape => shape !== undefined);
    if (strokes.length === 0) continue;

    // Render at least one, then stop starting new ones. What is left keeps its
    // cache entry unclaimed and is picked up next turn.
    if (rendered > 0 && performance.now() - startedAt > BUDGET_MS) {
      overBudget++;
      continue;
    }

    const image = await rasterizeInkCrop(strokes, {
      bounds,
      scale,
      renderBatch: opts.renderBatch,
    });
    if (image) {
      const crop: InkCrop = {
        label: cluster.label,
        image,
        w: Math.max(1, Math.round(bounds.w * scale)),
        h: Math.max(1, Math.round(bounds.h * scale)),
        changed: cluster.changed,
      };
      cache.set(key, crop);
      crops.push(crop);
      rendered++;
    } else {
      failed.add(key);
    }

    // Only after a real render; the steady state is all cache hits.
    if (opts.onCropBoundary && index < wanted.length - 1) {
      await opts.onCropBoundary();
    }
  }

  // Or the cache grows a JPEG per intermediate state of every line rewritten.
  for (const key of [...cache.keys()]) if (!live.has(key)) cache.delete(key);
  for (const key of [...failed]) if (!live.has(key)) failed.delete(key);

  // Rasterizing runs on the main thread inside a turn; this is how we find out
  // what a burst actually costs rather than guessing.
  // `console.debug`, not the app-wide logger: that one imports the auth store,
  // which reads localStorage at module load and does not belong in a leaf.
  console.debug("[inkCrops]", {
    clusters: wanted.length,
    rendered,
    cached: crops.length - rendered,
    overBudget,
    unrenderable,
    ms: Math.round(performance.now() - startedAt),
  });
  return crops;
}

/** Drop every cached crop for `owner` (test seam). */
export function resetInkCrops(owner: object): void {
  caches.delete(owner);
}
